import React, { useState, useEffect } from 'react';
import { Menu, X, Leaf, ShoppingBag, User, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { navigation } from '../mock';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Navbar = ({ onBook }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, login } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/80 backdrop-blur-lg shadow-[0_2px_20px_rgba(0,0,0,0.04)]' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="/" className="flex items-center gap-2 group">
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/92rpbx3r_nutrify_me_logo.png"
            alt="Nutrify Me logo"
            className="w-11 h-11 object-contain transition-transform group-hover:scale-105"
          />
          <div className="leading-none">
            <div className="font-serif text-xl text-[#1a2b17] tracking-tight">Nutrify Me</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#7a8471]">by Nutritionist Prachi</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {navigation.map((n) => (
            <a key={n.label} href={n.href} className="text-sm text-[#2a3a24] hover:text-[#c9743a] transition-colors relative group">
              {n.label}
              <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-[#c9743a] transition-all group-hover:w-full" />
            </a>
          ))}
          <button onClick={() => nav('/quiz')} className="text-sm text-[#c9743a] font-medium hover:underline underline-offset-4">
            Diet Quiz
          </button>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => setCartOpen(true)}
            className="relative w-11 h-11 rounded-full hover:bg-[#faf7f1] flex items-center justify-center transition"
          >
            <ShoppingBag className="w-4 h-4 text-[#1a2b17]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#c9743a] text-white text-[10px] flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <button onClick={() => nav('/dashboard')} className="flex items-center gap-2 pl-1 pr-4 py-1 rounded-full border border-neutral-200 hover:border-[#3a5a2c] transition">
              {user.picture ? <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" /> : <User className="w-4 h-4" />}
              <span className="text-sm text-[#1a2b17]">{user.name?.split(' ')[0]}</span>
            </button>
          ) : (
            <Button onClick={login} variant="outline" className="rounded-full h-10 gap-2">
              <LogIn className="w-4 h-4" /> Sign in
            </Button>
          )}

          <Button onClick={onBook} className="bg-[#3a5a2c] hover:bg-[#2d4622] text-white rounded-full px-5 h-10">
            Book Call
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button onClick={() => setCartOpen(true)} className="relative w-10 h-10 rounded-full flex items-center justify-center">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#c9743a] text-white text-[9px] flex items-center justify-center">{count}</span>}
          </button>
          <button className="text-[#1a2b17]" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-100 px-6 py-6 flex flex-col gap-4">
          {navigation.map((n) => (
            <a key={n.label} href={n.href} onClick={() => setOpen(false)} className="text-[#1a2b17] py-2 border-b border-neutral-100">{n.label}</a>
          ))}
          <button onClick={() => { setOpen(false); nav('/quiz'); }} className="text-left text-[#c9743a] py-2 border-b border-neutral-100">Diet Quiz</button>
          {user ? (
            <Button onClick={() => { setOpen(false); nav('/dashboard'); }} variant="outline" className="rounded-full">Dashboard</Button>
          ) : (
            <Button onClick={login} variant="outline" className="rounded-full">Sign in with Google</Button>
          )}
          <Button onClick={() => { setOpen(false); onBook(); }} className="bg-[#3a5a2c] hover:bg-[#2d4622] text-white rounded-full">Book Consultation</Button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
